// Leaderboard.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList } from "recharts";
import InventoryAPI from "../services/InventoryAPI"; // <-- adjust path if needed

// Replace these with your real GUIDs and labels
const EXPERIENCE_ITEMS = [
  { id: "dd88974c-be38-4a02-a1d2-544b05bc2be3", label: "Orbitals are Fun" },
  { id: "40cb39ee-5165-4ada-b2c6-381b622fbb0b", label: "Seeing With Sound" },
  { id: "49d8ca28-75ca-4c9c-b589-cb29fa068041", label: "Quantum Plants?!" },
  { id: "6082be29-d5a6-4c9a-90aa-91bfad1ae2b2", label: "AI Antibodies" },
];

const Leaderboard = () => {
  const [voteData, setVoteData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    let isCancelled = false;

    const loadVotes = async () => {
      setIsLoading(true);
      setLoadError(null);

      try {
        const responses = await Promise.all(
          EXPERIENCE_ITEMS.map(async (experienceItem) => {
            const response = await InventoryAPI.get(`/votes/${experienceItem.id}`, {
              requiresAuth: true,
            });

            // Adjust these keys to match your API response:
            const voteCount = response.data.data?.totalCoins ?? response.data?.votes ?? response.data?.count ?? 0;

            return {
              id: experienceItem.id,
              label: experienceItem.label,
              votes: voteCount,
            };
          })
        );

        if (!isCancelled) {
          const sortedData = [...responses].sort((firstItem, secondItem) => secondItem.votes - firstItem.votes);
          setVoteData(sortedData);
        }
      } catch (error) {
        console.error("Error loading votes:", error);
        if (!isCancelled) {
          setLoadError("Something went wrong while loading leaderboard data.");
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadVotes();

    return () => {
      isCancelled = true;
    };
  }, []);

  const totalVotes = voteData.reduce((sum, experienceItem) => sum + experienceItem.votes, 0);
  const maxVotes = voteData.reduce(
    (maxValue, experienceItem) => (experienceItem.votes > maxValue ? experienceItem.votes : maxValue),
    0
  );
  const topExperience = voteData[0];

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "var(--color-bg-surface, var(--color-bg-page, #f4f4f4))",
        backgroundAttachment: "fixed",
        backgroundPosition: "center top",
        backgroundSize: "cover",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          backgroundColor: "var(--color-bg-card, #ffffff)",
          borderBottom: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
          py: 2,
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="h5" fontWeight={600}>
            Leaderboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Poll results from your 10k Science experiences
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3, flexGrow: 1 }}>
        {isLoading ? (
          <Box
            sx={{
              height: "50vh",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : (
          <Stack spacing={2}>
            {/* Summary strip for screenshots */}
            <Paper
              elevation={0}
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                borderRadius: 0,
                border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
                backgroundColor: "var(--color-bg-card, #ffffff)",
              }}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Total votes
                </Typography>
                <Typography variant="h4" fontWeight={700}>
                  {totalVotes}
                </Typography>
              </Box>

              {topExperience && (
                <Box sx={{ textAlign: "right" }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Top experience
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    {topExperience.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {topExperience.votes} votes
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Poll-style results */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 0,
                border: "1px solid var(--color-border-subtle, rgba(0,0,0,0.12))",
                backgroundColor: "var(--color-bg-card, #ffffff)",
              }}
            >
              <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                Poll results
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Stack spacing={1.5}>
                {voteData.map((experienceItem, index) => {
                  const percentageOfMax = maxVotes > 0 ? (experienceItem.votes / maxVotes) * 100 : 0;
                  const percentageOfTotal = totalVotes > 0 ? (experienceItem.votes / totalVotes) * 100 : 0;

                  const isLeader = index === 0;

                  return (
                    <Box
                      key={experienceItem.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                      }}
                    >
                      <Box
                        sx={{
                          width: 24,
                          textAlign: "center",
                          fontWeight: 600,
                          color: isLeader ? "var(--color-action-success, #53be77)" : "inherit",
                        }}
                      >
                        {index + 1}
                      </Box>

                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={isLeader ? 700 : 500} sx={{ mb: 0.5 }}>
                          {experienceItem.label}
                        </Typography>

                        <Box
                          sx={{
                            height: 20,
                            backgroundColor: "var(--color-bg-highlight, #dddddd)",
                            borderRadius: 0,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              height: "100%",
                              width: `${percentageOfMax}%`,
                              background:
                                "linear-gradient(90deg, var(--color-action-primary, #239fc6), var(--color-action-primary-hover, #296f85))",
                              borderRadius: 0,
                              transition: "width 0.4s ease",
                            }}
                          />
                        </Box>
                      </Box>

                      <Box sx={{ textAlign: "right", minWidth: 72 }}>
                        <Typography variant="body2" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                          {experienceItem.votes}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                          {Math.round(percentageOfTotal)}%
                        </Typography>
                      </Box>
                    </Box>
                  );
                })}
              </Stack>
            </Paper>
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default Leaderboard;
